import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { format, formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, ChevronDown, ChevronRight, Copy, Search, UserPlus, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InviteClientDialog } from "@/components/advisor/InviteClientDialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SAGE } from "@shared/brand";

type ClientStatus = "new" | "active" | "stalled" | "complete";

interface AdvisorClient {
  id: string;
  name: string;
  email: string;
  activatedAt: string;
  lastLoginAt: string | null;
  estateReadyScore: { completed: number; total: number };
  overallCompletion: number;
  executorNamed: boolean;
  status: ClientStatus;
}

interface AdvisorInvite {
  id: string;
  email: string;
  name: string | null;
  status: string;
  sentAt: string;
}

interface AdvisorStats {
  totalClients: number;
  activeClients: number;
  stalledClients: number;
  newClients: number;
  completeClients: number;
  averageCompletion: number;
  discountCode: string;
  registrationLink: string;
}

const STATUS_BADGE: Record<ClientStatus, { label: string; className: string }> = {
  new: { label: "New", className: "bg-muted text-muted-foreground" },
  active: { label: "Active", className: "bg-[#4F6B51]/15 text-[#4F6B51]" },
  stalled: { label: "Stalled", className: "bg-stone-100 text-stone-600" },
  complete: { label: "Complete", className: "bg-primary/10 text-primary" },
};

function SmallRing({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? value / max : 0;
  const r = 10;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ;
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" className="inline-block">
      <circle cx="14" cy="14" r={r} fill="none" stroke="#E2E8F0" strokeWidth="3" />
      <circle
        cx="14" cy="14" r={r} fill="none" stroke="hsl(168 76% 42%)" strokeWidth="3"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 14 14)"
      />
      <text x="14" y="18" textAnchor="middle" fontSize="8" fill="#1A202C" fontWeight="600">
        {value}
      </text>
    </svg>
  );
}

export default function AdvisorDashboardPage() {
  const [, setLocation] = useLocation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<keyof AdvisorClient>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [pendingOpen, setPendingOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { toast } = useToast();

  // D-170.6: converted from a raw apiRequest. Class A — the same `/invite-client` route as the
  // dialog, and it inserts an advisorInvites row with no conflict guard, so a double-click on
  // Resend is a duplicate row plus a second email to someone else's inbox.
  const resendFlight = useSingleFlight();
  const resendInviteMutation = useMutation({
    mutationFn: async (vars: { email: string; name?: string }) =>
      await apiRequest("POST", "/api/advisor/invite-client", vars),
  });

  const { data: stats } = useQuery<AdvisorStats>({ queryKey: ["/api/advisor/stats"] });
  const { data: clients = [] } = useQuery<AdvisorClient[]>({ queryKey: ["/api/advisor/clients"] });
  const { data: invites = [] } = useQuery<AdvisorInvite[]>({ queryKey: ["/api/advisor/invites"] });

  const pendingInvites = invites.filter(i => i.status === "pending");

  const filtered = useMemo(() => {
    let list = [...clients];
    if (statusFilter !== "all") list = list.filter(c => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      let av: any = a[sortKey];
      let bv: any = b[sortKey];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [clients, statusFilter, search, sortKey, sortDir]);

  function toggleSort(key: keyof AdvisorClient) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  function SortHeader({ col, label }: { col: keyof AdvisorClient; label: string }) {
    return (
      <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(col)}>
        <span className="flex items-center gap-1">
          {label}
          {sortKey === col && <span className="text-xs">{sortDir === "asc" ? "↑" : "↓"}</span>}
        </span>
      </TableHead>
    );
  }

  function copyToClipboard(text: string, type: "code" | "link") {
    navigator.clipboard.writeText(text);
    if (type === "code") { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }
    else { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }
  }

  async function resendInvite(invite: AdvisorInvite) {
    try {
      const sent = await resendFlight.run(() =>
        resendInviteMutation.mutateAsync({ email: invite.email, name: invite.name ?? undefined }),
      );
      if (!sent) return; // duplicate fire — the original resend owns the toast
      toast({ title: "Invitation resent", description: `Re-sent to ${invite.email}` });
    } catch {
      toast({ title: "Error", description: "Failed to resend invitation", variant: "destructive" });
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Clients", value: stats?.totalClients ?? 0 },
          { label: "Active", value: stats?.activeClients ?? 0 },
          { label: "Stalled", value: stats?.stalledClients ?? 0 },
          { label: "Avg Completion", value: `${stats?.averageCompletion ?? 0}%` },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Discount code bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Your discount code</p>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-lg tracking-widest text-primary">
                  {stats?.discountCode ?? "—"}
                </span>
                <Button aria-label="Copy discount code" type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(stats?.discountCode ?? "", "code")}>
                  {codeCopied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-1">Registration link</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground truncate">{stats?.registrationLink ?? "—"}</span>
                <Button aria-label="Copy registration link" type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(stats?.registrationLink ?? "", "link")}>
                  {linkCopied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Button type="button" onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite a client
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Client table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-base">Clients</CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search name or email..."
                  className="pl-8 w-52"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="stalled">Stalled</TabsTrigger>
                  <TabsTrigger value="new">New</TabsTrigger>
                  <TabsTrigger value="complete">Complete</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader col="name" label="Client" />
                <SortHeader col="activatedAt" label="Activated" />
                <SortHeader col="lastLoginAt" label="Last Active" />
                <TableHead>Estate Ready</TableHead>
                <SortHeader col="overallCompletion" label="Completion" />
                <SortHeader col="executorNamed" label="Executor" />
                <SortHeader col="status" label="Status" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-12">
                    {clients.length === 0 ? (
                      <div className="flex flex-col items-center gap-3 text-center max-w-sm mx-auto">
                        <p className="text-base font-medium" style={{ color: SAGE[600] }}>No clients yet</p>
                        <p className="text-sm text-muted-foreground">
                          Send your first invite — your clients see your referral and you see their progress here.
                        </p>
                        <button
                          type="button"
                          className="text-sm font-medium underline underline-offset-4"
                          style={{ color: SAGE[600] }}
                          onClick={() => setInviteOpen(true)}
                        >
                          Send your first invite
                        </button>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground">No clients match your filter.</p>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(c => {
                const badge = STATUS_BADGE[c.status];
                return (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setLocation(`/client/${c.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(c.activatedAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.lastLoginAt
                        ? formatDistanceToNow(new Date(c.lastLoginAt), { addSuffix: true })
                        : "Never"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <SmallRing value={c.estateReadyScore.completed} max={c.estateReadyScore.total} />
                        <span className="text-sm text-muted-foreground">
                          {c.estateReadyScore.completed}/{c.estateReadyScore.total}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[80px]">
                        <Progress value={c.overallCompletion} className="h-2 w-16" />
                        <span className="text-sm text-muted-foreground">{c.overallCompletion}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={c.executorNamed ? "text-primary" : "text-muted-foreground"}>
                        {c.executorNamed ? <Check className="h-4 w-4 inline-block" aria-label="yes" /> : <X className="h-4 w-4 inline-block" aria-label="no" />}
                      </span>
                    </TableCell>
                    <TableCell>
                      {c.status === "stalled" ? (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className={`${badge.className} border-0 text-xs cursor-default`}>{badge.label}</Badge>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[200px] text-xs">
                              {c.name} hasn't logged in for 30+ days. Consider sending a follow-up.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge className={`${badge.className} border-0 text-xs`}>{badge.label}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {pendingInvites.length > 0 && (
        <Card>
          <CardHeader
            className="pb-2 cursor-pointer select-none"
            onClick={() => setPendingOpen(o => !o)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-muted-foreground">
                Pending Invitations ({pendingInvites.length})
              </CardTitle>
              {pendingOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CardHeader>
          {pendingOpen && (
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map(inv => (
                    <TableRow key={inv.id}>
                      <TableCell className="text-sm">{inv.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{inv.name ?? "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(inv.sentAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="sm" onClick={() => resendInvite(inv)}>
                          Resend
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      )}

      <InviteClientDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
